import classNames from 'classnames';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Input from '@/components/molecules/input/input';
import ToggleSwitch from '@/components/molecules/toggle-switch/toggle-switch';
import Container from '@/components/utilities/container/container';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './onboarding-flow-name-step-form-module.module.scss';

export interface OnboardingNameStepData {
  acceptedCommunication: boolean;
  firstName: string;
  lastName: string;
}

export type OnboardingFlowNameStepFormModuleProps = Omit<
  Content.Components['OnboardingFlowNameStepFormModuleDto'],
  '$type' | 'id' | 'pageId' | 'settings'
> & {
  /** The class name to apply */
  className?: string;
  /** Callback fired when the step is completed */
  onComplete?: (data: OnboardingNameStepData) => void;
};

const OnboardingFlowNameStepFormModule = ({
  buttonText,
  className,
  consentText,
  firstNameFieldEmptyValidationError,
  headline,
  lastNameFieldValidationError,
  marketingConsentText,
  onComplete,
}: OnboardingFlowNameStepFormModuleProps) => {
  const { t } = useI18n();

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<OnboardingNameStepData>({
    defaultValues: {
      acceptedCommunication: false,
      firstName: '',
      lastName: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    register('acceptedCommunication');
  }, [register]);

  const acceptedCommunication = watch('acceptedCommunication');

  const onSubmit: SubmitHandler<OnboardingNameStepData> = data => {
    onComplete?.(data);
  };

  return (
    <div className={classNames(styles.component, className)}>
      <Container className={styles.container}>
        <form
          autoComplete="on"
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography
            className={styles.headline}
            tagStyle="headingSmall"
            text={headline}
          />

          <div className={styles.inputs}>
            <Input
              {...register('firstName', {
                required:
                  firstNameFieldEmptyValidationError ||
                  t('items.form.validationRequired'),
                validate: value => value.trim() !== '',
              })}
              autoComplete="given-name"
              className={styles.input}
              error={errors.firstName?.message}
              id="firstName"
              label={t('items.modules.bookingFlow.firstName')}
              required
            />

            <Input
              {...register('lastName', {
                required:
                  lastNameFieldValidationError ||
                  t('items.form.validationRequired'),
                validate: value => value.trim() !== '',
              })}
              autoComplete="family-name"
              className={styles.input}
              error={errors.lastName?.message}
              id="lastName"
              label={t('items.modules.bookingFlow.lastName')}
              required
            />
          </div>

          {(consentText || marketingConsentText) && (
            <div className={styles.footNoteContainer}>
              {consentText && (
                <RichText className={styles.consentText} text={consentText} />
              )}

              {marketingConsentText && (
                <ToggleSwitch
                  checked={acceptedCommunication}
                  fullWidth
                  labelPosition="left"
                  onClick={() => {
                    setValue('acceptedCommunication', !acceptedCommunication, {
                      shouldDirty: true,
                    });
                  }}
                  type="button"
                >
                  <RichText
                    className={styles.toggleSwitchLabel}
                    text={marketingConsentText}
                  />
                </ToggleSwitch>
              )}
            </div>
          )}

          <Button className={styles.button} text={buttonText} />
        </form>
      </Container>
    </div>
  );
};

OnboardingFlowNameStepFormModule.displayName =
  'OnboardingFlowNameStepFormModule';

export default OnboardingFlowNameStepFormModule;
